import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Topbar, Sidebar, ToastHost } from '@aosenuma/ui';
import {
  getSupabase,
  NotificacionesDropdown,
  UserMenuDropdown,
  useTablesRealtime,
  useIsSuperAdmin,
  useSession,
  useUser,
  useCurrentUserProfile,
  ChangePasswordModal,
} from '@aosenuma/lib-supabase';
import '@aosenuma/lib-supabase/styles/comentarios.css';

const URL_LANDING = import.meta.env.VITE_PUBLIC_URL_LANDING ?? 'http://localhost:5173';
const URL_DASHBOARD = import.meta.env.VITE_PUBLIC_URL_DASHBOARD ?? 'http://localhost:5174';

if (import.meta.env.PROD) {
  if (!import.meta.env.VITE_PUBLIC_URL_LANDING) {
    throw new Error('VITE_PUBLIC_URL_LANDING requerido en producción');
  }
  if (!import.meta.env.VITE_PUBLIC_URL_DASHBOARD) {
    throw new Error('VITE_PUBLIC_URL_DASHBOARD requerido en producción');
  }
}

function handleLogout() {
  // PRIORIDAD: invalidar sesión LOCAL inmediato + redirect síncrono.
  //
  // Bug detectado 2026-05-11 por Diego: el await sb.auth.signOut() puede
  // colgar 5+ segundos por el Web Lock interno de gotrue-js (ver
  // AuthGate.tsx:191-207). Resultado: pantalla negra entre el click y el
  // redirect → riesgo de seguridad (el user creyendo logout completo
  // sigue con la sesión activa).
  //
  // Solución segura por defensa en profundidad:
  //   1. Limpiar localStorage + sessionStorage ANTES del signOut → JWT
  //      cacheado borrado inmediato. Cualquier intento de re-cargar la
  //      app encuentra "no session" y manda a login.
  //   2. signOut fire-and-forget (sin await) → invalida server-side cuando
  //      pueda. Si el Web Lock cuelga, no nos bloquea.
  //   3. window.location.replace (no href) → además impide volver con back
  //      button a una URL autenticada.
  if (typeof window !== 'undefined') {
    try { window.localStorage.clear(); } catch { /* */ }
    try { window.sessionStorage.clear(); } catch { /* */ }
  }
  void getSupabase().auth.signOut().catch(() => undefined);
  if (typeof window !== 'undefined') {
    window.location.replace(URL_LANDING);
  }
}

const NAV_ITEMS = [
  { key: 'bandeja',     to: '/',            label: 'Bandeja',     end: true,  icon: ico('M3 4h18v16H3z M3 10h18') },
  { key: 'captura',     to: '/captura',     label: 'Captura',     icon: ico('M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3z M9 3v15 M15 6v15') },
  { key: 'expedientes', to: '/expedientes', label: 'Expedientes', icon: ico('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6') },
  { key: 'visitas',     to: '/visitas',     label: 'Visitas',     icon: ico('M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z') },
  { key: 'bitacora',    to: '/bitacora',    label: 'Bitácora',    icon: ico('M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 7v5l3 3') },
];

// Solo visible para super-admins (los 3 emails hardcoded en useIsSuperAdmin
// + is_super_admin() en BD). RLS server-side garantiza que aunque alguien
// hackee el FE para ver el ítem, las RPCs rechazarán las acciones.
// Visible para administradores (admin_aosenuma; super-admins incluidos). NO técnicos
// — la carga masiva los confunde; ellos usan la captura individual.
const ADMIN_ITEMS = [
  { key: 'admin-capas', to: '/admin/capas', label: 'Capas',
    icon: ico('M12 2 22 8.5 12 15 2 8.5z M2 15.5 12 22 22 15.5') },
];

const ADMIN_ONLY_ITEMS = [
  { key: 'admin-accesos', to: '/admin/accesos', label: 'Accesos',
    icon: ico('M12 11c2.5 0 4.5-2 4.5-4.5S14.5 2 12 2 7.5 4 7.5 6.5 9.5 11 12 11z M5 21v-1a7 7 0 0 1 14 0v1') },
];

export function EquipoShell({ children }: { children: React.ReactNode }) {
  // Suscripción global a postgres_changes en gestiones/expedientes/visitas/
  // irregularidades. Cuando otro técnico (u otra pestaña del mismo user)
  // modifica datos, el bus invalida los caches y los hooks de esta SPA
  // refetch automáticamente. Una sola llamada por SPA — multiplexa internamente.
  useTablesRealtime();

  const loc = useLocation();
  const navigate = useNavigate();
  type ThemeChoice = 'light' | 'dark' | 'system';
  const [themeChoice, setThemeChoice] = useState<ThemeChoice>(() => {
    const s = localStorage.getItem('equipo-theme');
    if (s === 'dark' || s === 'system') return s;
    return 'light';
  });
  const [sysDark, setSysDark] = useState<boolean>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches,
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('equipo-sidebar-collapsed') === '1';
  });

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSysDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const dark = themeChoice === 'dark' || (themeChoice === 'system' && sysDark);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('equipo-theme', themeChoice);
  }, [dark, themeChoice]);

  useEffect(() => {
    localStorage.setItem('equipo-sidebar-collapsed', sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);

  const { user } = useUser();
  const { profile, refresh: refreshProfile } = useCurrentUserProfile();
  const [showChangePassword, setShowChangePassword] = useState(false);

  const isSuperAdmin = useIsSuperAdmin();
  const { rolApp } = useSession();
  const isAdmin = rolApp === 'admin_aosenuma'; // incluye super-admins (rol_app=admin_aosenuma)
  const navItemsBase = [
    ...NAV_ITEMS,
    ...(isAdmin ? ADMIN_ITEMS : []),
    ...(isSuperAdmin ? ADMIN_ONLY_ITEMS : []),
  ];
  const items = navItemsBase.map((it) => ({
    ...it,
    isActive: ('end' in it && it.end) ? loc.pathname === it.to : loc.pathname.startsWith(it.to),
  }));

  /**
   * Click en una notificación del dropdown → abrir Captura con esa parcela.
   */
  function handleSelectNotification(item: { parcela_codigo: string | null }) {
    if (!item.parcela_codigo) return;
    navigate(`/captura/${item.parcela_codigo}`);
  }

  /**
   * "Vista cliente" handoff cross-subdomain.
   *
   * IMPORTANTE: el window.open DEBE ejecutarse sincrónicamente desde el
   * click handler. Si lo metemos dentro de un await/promise, el browser
   * bloquea el popup ("popup-blocker") porque pierde el user gesture.
   * Por eso leemos la sesión sincrónicamente del localStorage en vez de
   * llamar a sb.auth.getSession() (que devuelve promise).
   */
  function openVistaCliente() {
    const sb = getSupabase();
    type MinSession = { access_token?: string; refresh_token?: string };
    let session: MinSession | null = null;
    try {
      const storageKey =
        (sb as unknown as { auth: { storageKey?: string } }).auth.storageKey
        ?? 'sb-tpvsetuadwuijczlqbtl-auth-token';
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const inner = parsed.currentSession as MinSession | undefined;
        session = inner ?? (parsed as MinSession);
      }
    } catch {
      session = null;
    }

    if (!session?.access_token || !session?.refresh_token) {
      // Fallback: dejar que el dashboard pida login. Se pierde el preview
      // hasta que el técnico se loguee ahí, pero no rompemos el flujo.
      window.open(`${URL_DASHBOARD}/?preview=cliente`, '_blank', 'noopener');
      return;
    }

    const url = new URL(`${URL_DASHBOARD}/auth/callback`);
    url.searchParams.set('next', '/?preview=cliente');
    const hash = new URLSearchParams({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      type: 'bearer',
    });
    url.hash = '#' + hash.toString();
    window.open(url.toString(), '_blank', 'noopener');
  }

  return (
    <div className="app-equipo" data-sidebar-collapsed={sidebarCollapsed ? 'true' : 'false'}>
      <Topbar
        title="Panel técnico"
        userChipContent={profile ? (
          <UserMenuDropdown
            user={profile}
            onChangePassword={() => setShowChangePassword(true)}
            onNicknameSaved={() => void refreshProfile()}
          />
        ) : null}
        rightSlot={
          <div className="topbar-actions">
            <NotificacionesDropdown onSelect={handleSelectNotification} />
            <button
              type="button"
              className="preview-cliente-btn"
              onClick={openVistaCliente}
              title="Abrir el panel del cliente en modo solo lectura (nueva pestaña, sesión activa)"
            >
              <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span>Vista cliente</span>
            </button>
            <div className="theme-pill" role="group" aria-label="Tema">
              {/* Sol — claro */}
              <button type="button" className={`theme-pill-btn${themeChoice === 'light' ? ' is-active' : ''}`} onClick={() => setThemeChoice('light')} title="Modo claro" aria-label="Modo claro">
                <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              </button>
              {/* Luna — oscuro */}
              <button type="button" className={`theme-pill-btn${themeChoice === 'dark' ? ' is-active' : ''}`} onClick={() => setThemeChoice('dark')} title="Modo oscuro" aria-label="Modo oscuro">
                <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              </button>
              {/* Monitor — sistema */}
              <button type="button" className={`theme-pill-btn${themeChoice === 'system' ? ' is-active' : ''}`} onClick={() => setThemeChoice('system')} title="Según el sistema" aria-label="Tema del sistema">
                <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </button>
            </div>
            <button
              type="button"
              className="change-password-btn"
              onClick={() => setShowChangePassword(true)}
              title="Cambiar contraseña"
              aria-label="Cambiar contraseña"
              disabled={!user?.email}
            >
              <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Contraseña</span>
            </button>
            <button
              type="button"
              className="logout-btn"
              onClick={() => { void handleLogout(); }}
              title="Cerrar sesión"
              aria-label="Cerrar sesión"
            >
              <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Salir</span>
            </button>
          </div>
        }
      />

      <Sidebar
        items={items}
        renderLink={(it, children) => (
          <NavLink to={it.to} end={(NAV_ITEMS.find((x) => x.key === it.key))?.end} style={{ display: 'block', textDecoration: 'none' }}>
            {children}
          </NavLink>
        )}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed((c) => !c)}
      />
      <main className="eq-workspace">{children}</main>
      <ToastHost />
      {showChangePassword && user?.email && (
        <ChangePasswordModal
          email={user.email}
          onClose={() => setShowChangePassword(false)}
        />
      )}
    </div>
  );
}

function ico(d: string) {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}
